/**
 * Archétypes produit — Lot L-UX-1 (étape 1).
 * Source normative : docs/platform/SPEC-ASSISTANT-FICHE-PRODUIT.md
 */

import { PRODUCT_SECTION_KEYS as S } from './produitSectionKeys.js';

/** @typedef {'default' | 'formule' | 'sauces'} OptionsPresentationMode */

/**
 * @typedef {Object} ProduitArchetype
 * @property {string} id
 * @property {string} label
 * @property {string} subtitle
 * @property {string[]} sections.visible
 * @property {string[]} sections.optional
 * @property {string[]} sections.hidden
 * @property {string[]} [sections.never]
 * @property {OptionsPresentationMode} [optionsMode]
 * @property {Object[]} [screen2Questions]
 * @property {Object} [prefill]
 * @property {{ commercant: string, product: string }} pilotExample
 */

export const ARCHETYPE_IDS = Object.freeze({
  SIMPLE: 'SIMPLE',
  AVEC_OPTIONS: 'AVEC_OPTIONS',
  SAUCES: 'SAUCES',
  POIDS: 'POIDS',
  FORMULE: 'FORMULE',
  PLATEAU_PIECES: 'PLATEAU_PIECES',
  PLANCHE_PERSONNES: 'PLANCHE_PERSONNES',
  PLANCHE_PERSO: 'PLANCHE_PERSO',
  INGREDIENT_PLANCHE: 'INGREDIENT_PLANCHE',
  RETAIL: 'RETAIL',
  AVANCE: 'AVANCE',
});

/** Cartes mises en avant par profil commerce (écran 1). */
export const COMMERCE_PROFILE_HIGHLIGHTS = Object.freeze({
  restauration: [
    ARCHETYPE_IDS.SIMPLE,
    ARCHETYPE_IDS.SAUCES,
    ARCHETYPE_IDS.PLATEAU_PIECES,
    ARCHETYPE_IDS.AVEC_OPTIONS,
    ARCHETYPE_IDS.FORMULE,
  ],
  traiteur: [
    ARCHETYPE_IDS.SIMPLE,
    ARCHETYPE_IDS.PLANCHE_PERSONNES,
    ARCHETYPE_IDS.PLANCHE_PERSO,
    ARCHETYPE_IDS.INGREDIENT_PLANCHE,
  ],
  formules: [
    ARCHETYPE_IDS.SIMPLE,
    ARCHETYPE_IDS.FORMULE,
    ARCHETYPE_IDS.AVEC_OPTIONS,
  ],
  retail: [ARCHETYPE_IDS.SIMPLE, ARCHETYPE_IDS.RETAIL, ARCHETYPE_IDS.AVEC_OPTIONS],
  default: [
    ARCHETYPE_IDS.SIMPLE,
    ARCHETYPE_IDS.AVEC_OPTIONS,
    ARCHETYPE_IDS.RETAIL,
    ARCHETYPE_IDS.AVANCE,
  ],
});

/** Map catégorie commerçant → profil highlights. */
export const CATEGORIE_TO_PROFILE = Object.freeze({
  RESTAURANT: 'restauration',
  CAFE: 'restauration',
  SUSHI: 'restauration',
  TRAITEUR: 'traiteur',
  PISCINE: 'retail',
  COMMERCE_NON_FOOD: 'retail',
});

/**
 * @type {Record<string, ProduitArchetype>}
 */
export const PRODUIT_ARCHETYPES = Object.freeze({
  [ARCHETYPE_IDS.SIMPLE]: {
    id: ARCHETYPE_IDS.SIMPLE,
    label: 'Produit simple',
    subtitle: 'Un prix, une photo, c’est tout',
    sections: {
      visible: [S.PHOTOS, S.INFOS, S.PRIX, S.CATEGORIE, S.STOCK],
      optional: [],
      hidden: [
        S.PLANCHE_PERSONNES,
        S.VENTE_AU_POIDS,
        S.INGREDIENTS,
        S.CHAMPS_METIER,
        S.COMPOSITION_PIECES,
        S.OPTIONS,
        S.BADGES,
        S.ELEMENTS_COMPOSABLES,
        S.LIEN_PLATEAUX,
        S.TEMPLATES,
      ],
    },
    screen2Questions: [],
    prefill: {},
    pilotExample: { commercant: 'Villoing Piscines', product: 'Chlore pastilles' },
  },

  [ARCHETYPE_IDS.AVEC_OPTIONS]: {
    id: ARCHETYPE_IDS.AVEC_OPTIONS,
    label: 'Produit avec choix',
    subtitle: 'Tailles, cuissons, variantes',
    sections: {
      visible: [S.PHOTOS, S.INFOS, S.PRIX, S.CATEGORIE, S.STOCK, S.OPTIONS],
      optional: [S.INGREDIENTS, S.BADGES],
      hidden: [
        S.PLANCHE_PERSONNES,
        S.VENTE_AU_POIDS,
        S.COMPOSITION_PIECES,
        S.ELEMENTS_COMPOSABLES,
        S.LIEN_PLATEAUX,
        S.TEMPLATES,
      ],
    },
    optionsMode: 'default',
    screen2Questions: [
      {
        id: 'optionGroupsCount',
        type: 'choice',
        label: 'Combien de groupes de choix ?',
        choices: [
          { value: '1', label: '1 groupe' },
          { value: '2', label: '2 groupes' },
          { value: '3+', label: '3 ou plus' },
        ],
        default: '1',
      },
    ],
    prefill: {},
    pilotExample: { commercant: 'Il Piatto', product: 'Pizza (taille)' },
  },

  [ARCHETYPE_IDS.SAUCES]: {
    id: ARCHETYPE_IDS.SAUCES,
    label: 'Sauces & suppléments',
    subtitle: 'Incluses, payantes, ou les deux',
    sections: {
      visible: [S.PHOTOS, S.INFOS, S.PRIX, S.CATEGORIE, S.STOCK, S.OPTIONS],
      optional: [S.INGREDIENTS, S.BADGES],
      hidden: [
        S.PLANCHE_PERSONNES,
        S.VENTE_AU_POIDS,
        S.COMPOSITION_PIECES,
        S.ELEMENTS_COMPOSABLES,
        S.LIEN_PLATEAUX,
        S.TEMPLATES,
      ],
    },
    optionsMode: 'sauces',
    screen2Questions: [
      {
        id: 'hasFreeSauces',
        type: 'boolean',
        label: 'Sauces ou accompagnements inclus (0 €) ?',
        default: true,
      },
      {
        id: 'hasPaidExtras',
        type: 'boolean',
        label: 'Suppléments payants ?',
        default: true,
      },
    ],
    prefill: {},
    pilotExample: { commercant: 'Sushi Lover', product: 'Bowl avec sauces' },
  },

  [ARCHETYPE_IDS.POIDS]: {
    id: ARCHETYPE_IDS.POIDS,
    label: 'Vente au poids',
    subtitle: 'Prix au kg, portion, découpe',
    sections: {
      visible: [S.PHOTOS, S.INFOS, S.VENTE_AU_POIDS, S.CATEGORIE, S.STOCK],
      optional: [S.OPTIONS, S.INGREDIENTS],
      hidden: [
        S.PRIX,
        S.PLANCHE_PERSONNES,
        S.COMPOSITION_PIECES,
        S.ELEMENTS_COMPOSABLES,
        S.LIEN_PLATEAUX,
        S.BADGES,
        S.TEMPLATES,
      ],
    },
    screen2Questions: [
      {
        id: 'unitePoids',
        type: 'choice',
        label: 'Unité de vente',
        choices: [
          { value: 'kg', label: 'Kilogramme (kg)' },
          { value: '100g', label: '100 grammes' },
        ],
        default: 'kg',
      },
    ],
    prefill: { venteAuPoids: true, prix: 0 },
    pilotExample: { commercant: 'Il Piatto', product: 'Charcuterie à la coupe' },
  },

  [ARCHETYPE_IDS.FORMULE]: {
    id: ARCHETYPE_IDS.FORMULE,
    label: 'Menu / formule',
    subtitle: 'Entrée, plat, dessert…',
    sections: {
      visible: [S.PHOTOS, S.INFOS, S.PRIX, S.CATEGORIE, S.STOCK, S.OPTIONS],
      optional: [S.INGREDIENTS, S.BADGES],
      hidden: [
        S.PLANCHE_PERSONNES,
        S.VENTE_AU_POIDS,
        S.COMPOSITION_PIECES,
        S.ELEMENTS_COMPOSABLES,
        S.LIEN_PLATEAUX,
        S.TEMPLATES,
      ],
    },
    optionsMode: 'formule',
    screen2Questions: [
      {
        id: 'formuleSteps',
        type: 'choice',
        label: 'Combien d’étapes compose le menu ?',
        choices: [
          { value: '2', label: '2 (ex. Plat + Dessert)' },
          { value: '3', label: '3 (Entrée + Plat + Dessert)' },
          { value: '4+', label: '4 ou plus' },
        ],
        default: '3',
      },
    ],
    prefill: { productType: 'MENU_COMBO' },
    pilotExample: { commercant: 'Au Bon Coin', product: 'Menu personnalisé' },
  },

  [ARCHETYPE_IDS.PLATEAU_PIECES]: {
    id: ARCHETYPE_IDS.PLATEAU_PIECES,
    label: 'Plateau à composer (pièces)',
    subtitle: 'Sushi, plateau par pièces',
    sections: {
      visible: [
        S.PHOTOS,
        S.INFOS,
        S.CATEGORIE,
        S.STOCK,
        S.COMPOSITION_PIECES,
        S.ELEMENTS_COMPOSABLES,
      ],
      optional: [S.PRIX, S.INGREDIENTS, S.BADGES, S.OPTIONS],
      hidden: [S.PLANCHE_PERSONNES, S.VENTE_AU_POIDS, S.LIEN_PLATEAUX, S.TEMPLATES],
    },
    screen2Questions: [
      {
        id: 'validationMode',
        type: 'choice',
        label: 'Règle de remplissage du plateau',
        choices: [
          { value: 'EXACT', label: 'Exactement X pièces' },
          { value: 'MINIMUM', label: 'Au minimum X pièces' },
          { value: 'FLEXIBLE', label: 'Au plus X pièces' },
        ],
        default: 'EXACT',
      },
      {
        id: 'incrementPieces',
        type: 'choice',
        label: 'Incrément de commande par pièce',
        choices: [
          { value: '1', label: '1 par 1' },
          { value: '2', label: 'Par 2' },
        ],
        default: '1',
      },
    ],
    prefill: { modeComposition: true, validationMode: 'EXACT', incrementPieces: 1 },
    pilotExample: { commercant: 'Sushi Lover', product: 'Composer votre plateau' },
  },

  [ARCHETYPE_IDS.PLANCHE_PERSONNES]: {
    id: ARCHETYPE_IDS.PLANCHE_PERSONNES,
    label: 'Planche traiteur (fixe)',
    subtitle: 'Prix par nombre de personnes',
    sections: {
      visible: [S.PHOTOS, S.INFOS, S.CATEGORIE, S.STOCK, S.PLANCHE_PERSONNES],
      optional: [S.INGREDIENTS, S.BADGES],
      hidden: [
        S.PRIX,
        S.VENTE_AU_POIDS,
        S.COMPOSITION_PIECES,
        S.ELEMENTS_COMPOSABLES,
        S.OPTIONS,
        S.LIEN_PLATEAUX,
        S.TEMPLATES,
      ],
    },
    screen2Questions: [
      {
        id: 'minPersons',
        type: 'number',
        label: 'Minimum de personnes',
        default: 2,
        min: 1,
        max: 50,
      },
      {
        id: 'maxPersons',
        type: 'number',
        label: 'Maximum de personnes',
        default: 10,
        min: 1,
        max: 50,
      },
    ],
    prefill: {
      isPlateauParPersonne: true,
      productType: 'PAR_PERSONNE',
      compositionMode: 'FIXED',
    },
    pilotExample: { commercant: 'Il Piatto', product: 'Planche mixte 4 personnes' },
  },

  [ARCHETYPE_IDS.PLANCHE_PERSO]: {
    id: ARCHETYPE_IDS.PLANCHE_PERSO,
    label: 'Planche personnalisable',
    subtitle: 'Le client compose sa planche',
    sections: {
      visible: [S.PHOTOS, S.INFOS, S.CATEGORIE, S.STOCK, S.PLANCHE_PERSONNES],
      optional: [S.INGREDIENTS, S.BADGES, S.OPTIONS],
      hidden: [
        S.PRIX,
        S.VENTE_AU_POIDS,
        S.COMPOSITION_PIECES,
        S.ELEMENTS_COMPOSABLES,
        S.TEMPLATES,
      ],
    },
    screen2Questions: [
      {
        id: 'minPersons',
        type: 'number',
        label: 'Minimum de personnes',
        default: 2,
        min: 1,
        max: 50,
      },
      {
        id: 'maxPersons',
        type: 'number',
        label: 'Maximum de personnes',
        default: 20,
        min: 1,
        max: 50,
      },
    ],
    prefill: {
      isPlateauParPersonne: true,
      productType: 'PAR_PERSONNE',
      compositionMode: 'COMPOSABLE',
    },
    pilotExample: { commercant: 'Il Piatto', product: 'Planche perso charcuterie' },
  },

  [ARCHETYPE_IDS.INGREDIENT_PLANCHE]: {
    id: ARCHETYPE_IDS.INGREDIENT_PLANCHE,
    label: 'Produit pour planche',
    subtitle: 'Ex. : coppa, fromage — proposé sur une planche personnalisable',
    sections: {
      visible: [S.PHOTOS, S.INFOS, S.PRIX, S.CATEGORIE, S.STOCK, S.LIEN_PLATEAUX],
      optional: [S.INGREDIENTS],
      hidden: [
        S.PLANCHE_PERSONNES,
        S.VENTE_AU_POIDS,
        S.COMPOSITION_PIECES,
        S.ELEMENTS_COMPOSABLES,
        S.OPTIONS,
        S.BADGES,
        S.TEMPLATES,
      ],
    },
    screen2Questions: [],
    prefill: {},
    pilotExample: { commercant: 'Il Piatto', product: 'Coppa di Parma' },
  },

  [ARCHETYPE_IDS.RETAIL]: {
    id: ARCHETYPE_IDS.RETAIL,
    label: 'Produit magasin',
    subtitle: 'Article technique ou retail',
    sections: {
      visible: [S.PHOTOS, S.INFOS, S.PRIX, S.CATEGORIE, S.STOCK],
      optional: [S.OPTIONS, S.CHAMPS_METIER],
      hidden: [
        S.PLANCHE_PERSONNES,
        S.VENTE_AU_POIDS,
        S.INGREDIENTS,
        S.COMPOSITION_PIECES,
        S.BADGES,
        S.ELEMENTS_COMPOSABLES,
        S.LIEN_PLATEAUX,
        S.TEMPLATES,
      ],
    },
    screen2Questions: [],
    prefill: { productType: 'COMMERCE_NON_FOOD' },
    pilotExample: { commercant: 'Villoing Piscines', product: 'Chlore galets' },
  },

  [ARCHETYPE_IDS.AVANCE]: {
    id: ARCHETYPE_IDS.AVANCE,
    label: 'Mode expert',
    subtitle: 'Toutes les sections (état actuel)',
    sections: {
      visible: [
        S.PHOTOS,
        S.INFOS,
        S.PRIX,
        S.VENTE_AU_POIDS,
        S.PLANCHE_PERSONNES,
        S.INGREDIENTS,
        S.CATEGORIE,
        S.CHAMPS_METIER,
        S.STOCK,
        S.COMPOSITION_PIECES,
        S.OPTIONS,
        S.BADGES,
        S.ELEMENTS_COMPOSABLES,
        S.LIEN_PLATEAUX,
        S.TEMPLATES,
      ],
      optional: [],
      hidden: [],
    },
    optionsMode: 'default',
    screen2Questions: [],
    prefill: {},
    pilotExample: { commercant: '—', product: 'Cas hybride / support' },
  },
});

/** Liste ordonnée pour affichage écran 1. */
export const ARCHETYPE_LIST = Object.freeze(
  Object.values(PRODUIT_ARCHETYPES).filter((a) => a.id !== ARCHETYPE_IDS.AVANCE)
);

export function getArchetypeById(archetypeId) {
  return PRODUIT_ARCHETYPES[archetypeId] ?? null;
}

export function getHighlightProfileForCategorie(categorie) {
  if (!categorie) return 'default';
  return CATEGORIE_TO_PROFILE[categorie] ?? 'default';
}

export function getHighlightedArchetypeIds(categorie) {
  const profile = getHighlightProfileForCategorie(categorie);
  return COMMERCE_PROFILE_HIGHLIGHTS[profile] ?? COMMERCE_PROFILE_HIGHLIGHTS.default;
}

export function shouldSkipScreen2(archetypeId) {
  const archetype = getArchetypeById(archetypeId);
  return !archetype?.screen2Questions?.length;
}
